import { ROUTES } from '../config/routes';
import { getUser } from './auth';

export const STUDENT_PROFILE_COMPLETION_ALERT_THRESHOLD = 80;
export const STUDENT_PROFILE_LOGIN_ALERT_KEY = 'studenthub:student-profile-login-alert';
export const PROFILE_COMPLETION_ALERT_QUERY = 'showProfileCompletionAlert';
export const PROFILE_COMPLETION_PERCENT_QUERY = 'profileCompletion';

export interface StudentProfileLoginAlertPayload {
  total: number;
}

/** Parse completion % from login user payload (camelCase or snake_case). */
export function parseProfileCompletionPercent(source: unknown): number | null {
  if (source == null || typeof source !== 'object') return null;
  const record = source as Record<string, unknown>;
  const raw =
    record.profileCompletionPercent ?? record.profile_completion_percent;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.max(0, Math.min(100, Math.round(raw)));
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) {
      return Math.max(0, Math.min(100, Math.round(parsed)));
    }
  }
  return null;
}

export function shouldShowProfileCompletionAlert(source: unknown): boolean {
  if (source == null || typeof source !== 'object') return false;
  const record = source as Record<string, unknown>;
  const flag = record.showProfileCompletionAlert ?? record.show_profile_completion_alert;
  if (flag === true || flag === 1 || flag === '1' || flag === 'true') return true;
  const percent = parseProfileCompletionPercent(record);
  return percent != null && percent < STUDENT_PROFILE_COMPLETION_ALERT_THRESHOLD;
}

export function getLoginUserRole(source: unknown): string | null {
  if (source == null || typeof source !== 'object') return null;
  const record = source as Record<string, unknown>;
  const raw = record.role ?? record.role_name ?? record.roleName;
  if (raw == null || raw === '') return null;
  return String(raw).toUpperCase();
}

/** Normalize login user object so session has completion fields from login API. */
export function normalizeLoginUserData(
  data: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (!data) return {};
  const normalized: Record<string, unknown> = { ...data };
  const role = getLoginUserRole(normalized);
  if (role) {
    normalized.role = role;
  }

  const userUniqueId =
    data.userUniqueId ??
    data.user_unique_id ??
    data.userUniqId;
  if (userUniqueId != null && String(userUniqueId).trim() !== '') {
    const uid = String(userUniqueId).trim();
    normalized.userUniqueId = uid;
    normalized.user_unique_id = uid;
  }

  const percent = parseProfileCompletionPercent(data);
  if (percent == null) return normalized;

  normalized.profileCompletionPercent = percent;
  normalized.profile_completion_percent = percent;
  const showAlert = percent < STUDENT_PROFILE_COMPLETION_ALERT_THRESHOLD;
  normalized.showProfileCompletionAlert = showAlert;
  normalized.show_profile_completion_alert = showAlert;
  if (showAlert) {
    stashProfileLoginAlert(percent);
  }
  return normalized;
}

export function readStudentProfileLoginAlert(): StudentProfileLoginAlertPayload | null {
  for (const storage of [sessionStorage, localStorage]) {
    try {
      const raw = storage.getItem(STUDENT_PROFILE_LOGIN_ALERT_KEY);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as StudentProfileLoginAlertPayload;
      if (typeof parsed?.total === 'number') return parsed;
    } catch {
      /* try next storage */
    }
  }
  return null;
}

export function clearStudentProfileLoginAlert(): void {
  sessionStorage.removeItem(STUDENT_PROFILE_LOGIN_ALERT_KEY);
  localStorage.removeItem(STUDENT_PROFILE_LOGIN_ALERT_KEY);
}

/** Apply a canonical completion % to session, alert stash, and URL (when on dashboard). */
export function applyStudentProfileCompletionPercent(percent: number): void {
  const rounded = Math.max(0, Math.min(100, Math.round(percent)));
  syncProfileCompletionToSession(rounded);
  if (rounded < STUDENT_PROFILE_COMPLETION_ALERT_THRESHOLD) {
    stashProfileLoginAlert(rounded);
  } else {
    clearStudentProfileLoginAlert();
  }
}

/**
 * If calculated % differs from session/DB, save via API and update session + navbar.
 * Returns the canonical percent now in use.
 */
export async function persistProfileCompletionPercentIfChanged(
  calculatedPercent: number,
  saveToDb: (percent: number) => Promise<number | undefined>
): Promise<number> {
  const rounded = Math.max(0, Math.min(100, Math.round(calculatedPercent)));
  const stored = parseProfileCompletionPercent(getUser());

  if (stored === rounded) {
    applyStudentProfileCompletionPercent(rounded);
    return rounded;
  }

  try {
    const saved = await saveToDb(rounded);
    const final = typeof saved === 'number' ? saved : rounded;
    applyStudentProfileCompletionPercent(final);
    return final;
  } catch {
    applyStudentProfileCompletionPercent(rounded);
    return rounded;
  }
}

export function syncProfileCompletionToSession(percent: number): void {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return;
    const cur = JSON.parse(raw) as Record<string, unknown>;
    const rounded = Math.max(0, Math.min(100, Math.round(percent)));
    const showAlert = rounded < STUDENT_PROFILE_COMPLETION_ALERT_THRESHOLD;
    const prevPercent = parseProfileCompletionPercent(cur);
    const prevShowAlert = shouldShowProfileCompletionAlert(cur);
    if (prevPercent === rounded && prevShowAlert === showAlert) return;
    const updated = {
      ...cur,
      profileCompletionPercent: rounded,
      profile_completion_percent: rounded,
      showProfileCompletionAlert: showAlert,
      show_profile_completion_alert: showAlert,
    };
    localStorage.setItem('user', JSON.stringify(updated));
    window.dispatchEvent(new Event('authChange'));
  } catch {
    /* ignore */
  }
}

export function stashProfileLoginAlert(percent: number): void {
  const payload = JSON.stringify({ total: percent });
  sessionStorage.setItem(STUDENT_PROFILE_LOGIN_ALERT_KEY, payload);
  localStorage.setItem(STUDENT_PROFILE_LOGIN_ALERT_KEY, payload);
}

export function readProfileCompletionAlertFromSearchParams(
  searchParams: URLSearchParams
): StudentProfileLoginAlertPayload | null {
  const flag = searchParams.get(PROFILE_COMPLETION_ALERT_QUERY);
  if (flag !== '1' && flag !== 'true') return null;
  const raw = searchParams.get(PROFILE_COMPLETION_PERCENT_QUERY);
  const percent = raw != null ? Number(raw) : NaN;
  if (!Number.isFinite(percent)) return null;
  return { total: Math.max(0, Math.min(100, Math.round(percent))) };
}

/**
 * Post-login route from login API profile_completion_percent:
 * Below 80 → dashboard + completion modal; else dashboard only.
 */
export function buildStudentPostLoginUrl(
  profileCompletionPercent?: number | null
): string {
  const percent =
    profileCompletionPercent != null && Number.isFinite(profileCompletionPercent)
      ? Math.round(profileCompletionPercent)
      : null;

  if (percent == null) {
    clearStudentProfileLoginAlert();
    return ROUTES.STUDENT.DASHBOARD;
  }

  if (percent < STUDENT_PROFILE_COMPLETION_ALERT_THRESHOLD) {
    stashProfileLoginAlert(percent);
    const qs = new URLSearchParams({
      [PROFILE_COMPLETION_ALERT_QUERY]: '1',
      [PROFILE_COMPLETION_PERCENT_QUERY]: String(percent),
    });
    return `${ROUTES.STUDENT.DASHBOARD}?${qs.toString()}`;
  }

  clearStudentProfileLoginAlert();
  return ROUTES.STUDENT.DASHBOARD;
}

/** Remove post-login alert query params from a URLSearchParams instance. */
export function stripProfileCompletionAlertParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(PROFILE_COMPLETION_ALERT_QUERY);
  next.delete(PROFILE_COMPLETION_PERCENT_QUERY);
  return next;
}

/** Navigate immediately after student login (no extra profile API calls). */
export function navigateAfterStudentLogin(userData: Record<string, unknown>): void {
  const role = getLoginUserRole(userData);
  if (role !== 'STUDENT') return;
  const percent = parseProfileCompletionPercent(userData);
  window.location.href = buildStudentPostLoginUrl(percent);
}

export function resolveProfileCompletionLoginAlert(
  searchParams?: URLSearchParams
): StudentProfileLoginAlertPayload | null {
  const fromStorage = readStudentProfileLoginAlert();
  if (fromStorage) return fromStorage;

  if (searchParams) {
    const fromUrl = readProfileCompletionAlertFromSearchParams(searchParams);
    if (fromUrl) return fromUrl;
  }

  const user = getUser();
  if (shouldShowProfileCompletionAlert(user)) {
    const percent = parseProfileCompletionPercent(user);
    if (percent != null) return { total: percent };
  }

  return null;
}
